import React, { useState } from 'react';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/GoogleSearchTrends.css';
import { API_BASE_URL } from '../../config';

// Add countries data
const countries = [
  { code: '', name: 'Worldwide' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IN', name: 'India' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'BR', name: 'Brazil' },
  { code: 'JP', name: 'Japan' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'MX', name: 'Mexico' },
  { code: 'KR', name: 'South Korea' },
  { code: 'RU', name: 'Russia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' },
].sort((a, b) => a.name.localeCompare(b.name));

function GoogleSearchTrends({ email, onLogout }) {
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [timeframe, setTimeframe] = useState('today 12-m');
  const [geo, setGeo] = useState('US');
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTrendData = async (endpoint) => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE_URL}/trends/${endpoint}`;
      if (endpoint === 'interest-over-time') {
        url += `?keyword=${searchKeyword}&timeframe=${timeframe}&geo=${geo}`;
      } else if (endpoint === 'related-topics' || endpoint === 'related-queries') {
        url += `?keyword=${searchKeyword}&geo=${geo}`;
      } else if (endpoint === 'compare-keywords') {
        url += `?keywords=${searchKeyword}&timeframe=${timeframe}&geo=${geo}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch trend data');
      }
      const data = await response.json();
      setTrendData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTrendSelect = (trend) => {
    setSelectedTrend(trend);
    if (trend === 'realtime') {
      fetchTrendData('realtime');
    } else if (searchKeyword) {
      fetchTrendData(trend);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedTrend && searchKeyword) {
      fetchTrendData(selectedTrend);
    }
  };

  const renderTrendContent = () => {
    if (loading) {
      return <div className="trend-loading">Loading...</div>;
    }

    if (error) {
      return <div className="trend-error">{error}</div>;
    }

    if (!selectedTrend) {
      return null;
    }

    if (!trendData) {
      return (
        <div className="trend-empty">
          {selectedTrend === 'realtime' ? 
            'Loading realtime trends...' : 
            'Enter a keyword and click search to view trends'}
        </div>
      );
    }

    switch (selectedTrend) {
      case 'interest-over-time':
        return (
          <div className="trend-data">
            <h3>Search Interest Over Time</h3>
            <div className="trend-chart">
              {Object.entries(trendData.data).map(([date, value]) => (
                <div key={date} className="trend-bar" style={{ height: `${value}%` }}>
                  <span className="trend-value">{value}</span>
                  <span className="trend-date">{new Date(date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'related-topics':
        return (
          <div className="trend-data">
            <div className="trend-section">
              <h3>Rising Topics</h3>
              <div className="trend-list">
                {trendData.rising.map((topic, index) => (
                  <div key={index} className="trend-item">
                    <span className="trend-title">{topic.topic_title}</span>
                    <span className="trend-type">{topic.topic_type}</span>
                    <span className="trend-value">+{topic.value}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="trend-section">
              <h3>Top Topics</h3>
              <div className="trend-list">
                {trendData.top.map((topic, index) => (
                  <div key={index} className="trend-item">
                    <span className="trend-title">{topic.topic_title}</span>
                    <span className="trend-type">{topic.topic_type}</span>
                    <span className="trend-value">{topic.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'related-queries':
        return (
          <div className="trend-data">
            <div className="trend-section">
              <h3>Rising Queries</h3>
              <div className="trend-list">
                {trendData.rising.map((query, index) => (
                  <div key={index} className="trend-item">
                    <span className="trend-title">{query.query}</span>
                    <span className="trend-value">+{query.value}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="trend-section">
              <h3>Top Queries</h3>
              <div className="trend-list">
                {trendData.top.map((query, index) => (
                  <div key={index} className="trend-item">
                    <span className="trend-title">{query.query}</span>
                    <span className="trend-value">{query.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'realtime':
        return (
          <div className="trend-data">
            <h3>Realtime Trends</h3>
            <div className="trend-list">
              {trendData.trending_searches.map((search, index) => (
                <div key={index} className="trend-item">
                  <span className="trend-title">{search}</span>
                  <span className="trend-rank">#{index + 1}</span>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Google Search Trends" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="trends">
          <div className="trends-controls">
            <div className="search-controls">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  placeholder="Enter keyword to analyze..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="search-input"
                  required
                />
                <select 
                  value={timeframe} 
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="timeframe-select"
                >
                  <option value="now 1-H">Past hour</option>
                  <option value="now 1-d">Past day</option>
                  <option value="now 7-d">Past week</option>
                  <option value="today 1-m">Past month</option>
                  <option value="today 12-m">Past year</option>
                  <option value="today 5-y">Past 5 years</option>
                </select>
                <select 
                  value={geo} 
                  onChange={(e) => setGeo(e.target.value)}
                  className="geo-select"
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <button type="submit" className="search-button">
                  Search
                </button>
              </form>
            </div>
          </div>

          <div className="trends-grid">
            <div 
              className={`trend-card ${selectedTrend === 'interest-over-time' ? 'active' : ''}`}
              onClick={() => handleTrendSelect('interest-over-time')}
            >
              <div className="trend-icon">📈</div>
              <h3>Search Volume Analysis</h3>
              <p>Track search volume trends over time</p>
            </div>

            <div 
              className={`trend-card ${selectedTrend === 'related-topics' ? 'active' : ''}`}
              onClick={() => handleTrendSelect('related-topics')}
            >
              <div className="trend-icon">🔍</div>
              <h3>Related Topics</h3>
              <p>Discover related topics and categories</p>
            </div>

            <div 
              className={`trend-card ${selectedTrend === 'related-queries' ? 'active' : ''}`}
              onClick={() => handleTrendSelect('related-queries')}
            >
              <div className="trend-icon">📊</div>
              <h3>Related Queries</h3>
              <p>Find related search terms and phrases</p>
            </div>

            <div 
              className={`trend-card ${selectedTrend === 'realtime' ? 'active' : ''}`}
              onClick={() => handleTrendSelect('realtime')}
            >
              <div className="trend-icon">🔥</div>
              <h3>Realtime Trends</h3>
              <p>See what's trending right now</p>
            </div>
          </div>

          <div className="trend-content">
            {renderTrendContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoogleSearchTrends; 